'use client';
import type { ComponentProps } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import NextImage from 'next/image';
import { Flex } from '@chakra-ui/react';
import { SafeLink } from '@/components/ui/SafeLink';

import WishListButton from './components/WishListButton';
import ProfileButton from './components/ProfileButton';
import CountryButton from './components/CountryButton';
import HeaderComboboxSearchInput from './components/HeaderComboboxSearchInput/HeaderComboboxSearchInput';
import HeaderInput from './components/HeaderComboboxSearchInput/HeaderInput';
import { useCartDrawer } from '@/contexts/cartDrawerContext';
import CountrySettingModal from '@/components/country/CountrySettingModal';

import { useEventListener } from '@/hooks/utils';
import { FREE_SHIPPING_BANNER_HEIGHT } from '../Header';
import ShoppingBagButton from './components/ShoppingBagButton';

const HeaderContentIconButton = ({ ...rest }: ComponentProps<typeof Flex>) => {
  return (
    <Flex
      width={{ base: '20%', mobile: '25px', tablet: '20%', desktop: '17%' }}
      alignItems="center"
      justifyContent="center"
      {...rest}
    />
  );
};

const HeaderContent = () => {
  const { onCartOpen } = useCartDrawer();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const checkScrollPosition = useCallback(() => {
    if (window.scrollY > FREE_SHIPPING_BANNER_HEIGHT) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  }, []);

  useEffect(() => {
    checkScrollPosition();
  }, [checkScrollPosition]);

  useEventListener('scroll', checkScrollPosition);

  return (
    <Flex
      as="nav"
      flexDirection="column"
      base={{
        height: 'fit-content',
        minHeight: '48px',
      }}
      desktop={{
        height: 'fit-content',
        minHeight: '60px',
      }}
      width="100%"
    >
      <Flex
        backgroundColor="black"
        boxShadow={isScrolled ? 'md' : 'none'}
        justifyContent="center"
        alignItems="center"
        left="0"
        paddingY={{
          base: '6px',
          mobile: '6px',
          tablet: 0,
          desktop: 0,
        }}
        position={isScrolled ? 'fixed' : 'relative'}
        top="0"
        width="100%"
        zIndex={isScrolled ? 'docked' : 'auto'}
      >
        <Flex alignItems="center" justifyContent="center" maxWidth="1280px" width="100%">
          <Flex
            alignItems="center"
            justifyContent={{ base: 'center', tablet: 'flex-start' }}
            maxWidth={{
              base: '108px',
              tablet: '100%',
            }}
            width={{
              base: '15%',
              tablet: '23%',
              desktop: '20%',
            }}
            paddingY="7px"
          >
            <SafeLink href="/">
              <Flex display={{ base: 'block', tablet: 'none' }}>
                <NextImage
                  alt="SkinSeoul"
                  height={0}
                  src="https://skin-seoul.com/wp-content/uploads/2025/01/skin-seoul-logo.png"
                  width={32}
                  style={{
                    width: 'auto',
                    height: '35px',
                  }}
                />
              </Flex>

              <Flex
                display={{ base: 'none', tablet: 'block' }}
                width={{
                  tablet: '84%',
                  desktop: '81%',
                }}
              >
                <NextImage
                  alt="SkinSeoul"
                  height={90}
                  src="https://skin-seoul.com/wp-content/uploads/2024/12/desktop-logo.png"
                  width={250}
                />
              </Flex>
            </SafeLink>
          </Flex>
          <Flex
            width={{
              base: '60%',
              mobile: '60%',
              tablet: '52.726%',
              desktop: '60%',
            }}
          >
            <Flex width="100%" display={{ base: 'none', tablet: 'block', desktop: 'block' }}>
              <HeaderComboboxSearchInput />
            </Flex>
            <Flex asChild width="100%" display={{ base: 'block', tablet: 'none', desktop: 'none' }}>
              <SafeLink href="/search">
                <HeaderInput readOnly userSelect="none" />
              </SafeLink>
            </Flex>
          </Flex>
          <Flex
            alignItems="center"
            color="white"
            gap={{ base: '20px', tablet: '7px', desktop: '7px' }}
            justifyContent={{ base: 'center', tablet: 'flex-end' }}
            width={{
              base: '25%',
              tablet: '23%',
              desktop: '20%',
            }}
          >
            <HeaderContentIconButton display={{ base: 'none', tablet: 'flex', desktop: 'flex' }}>
              <SafeLink href="/wishlist">
                <WishListButton />
              </SafeLink>
            </HeaderContentIconButton>
            <HeaderContentIconButton>
              <ShoppingBagButton onClick={() => onCartOpen()} />
            </HeaderContentIconButton>

            <HeaderContentIconButton display={{ base: 'none', tablet: 'flex', desktop: 'flex' }}>
              <SafeLink href="/my-account">
                <ProfileButton />
              </SafeLink>
            </HeaderContentIconButton>

            <HeaderContentIconButton>
              <CountryButton onClick={() => setIsOpen(true)} />
              <CountrySettingModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
            </HeaderContentIconButton>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default HeaderContent;
